/**
 * BigNumber Utility Functions
 * Uses big.js library to handle precise decimal arithmetic
 * Prevents floating-point precision issues in JavaScript
 */
import Big from 'big.js'

/**
 * Safely convert a value to Big number
 * Returns Big(0) if conversion fails
 */
export const toBig = (value: any): Big => {
  try {
    if (value === null || value === undefined || value === '') {
      return new Big(0)
    }
    return new Big(value)
  } catch (error) {
    console.warn(`Failed to convert value to Big number: ${value}`, error)
    return new Big(0)
  }
}

/**
 * Add two numbers with precision
 */
export const add = (a: any, b: any): Big => {
  return toBig(a).plus(toBig(b))
}

/**
 * Subtract two numbers with precision
 */
export const subtract = (a: any, b: any): Big => {
  return toBig(a).minus(toBig(b))
}

/**
 * Multiply two numbers with precision
 */
export const multiply = (a: any, b: any): Big => {
  return toBig(a).times(toBig(b))
}

/**
 * Divide two numbers with precision
 */
export const divide = (a: any, b: any): Big => {
  const divisor = toBig(b)
  if (divisor.eq(0)) {
    console.warn('Division by zero attempted, returning 0')
    return new Big(0)
  }
  return toBig(a).div(divisor)
}

/**
 * Compare two numbers
 * Returns: 1 if a > b, -1 if a < b, 0 if a === b
 */
export const compare = (a: any, b: any): number => {
  return toBig(a).cmp(toBig(b))
}

/**
 * Check if a > b
 */
export const greaterThan = (a: any, b: any): boolean => {
  return toBig(a).gt(toBig(b))
}

/**
 * Check if a >= b
 */
export const greaterThanOrEqual = (a: any, b: any): boolean => {
  return toBig(a).gte(toBig(b))
}

/**
 * Check if a < b
 */
export const lessThan = (a: any, b: any): boolean => {
  return toBig(a).lt(toBig(b))
}

/**
 * Check if a <= b
 */
export const lessThanOrEqual = (a: any, b: any): boolean => {
  return toBig(a).lte(toBig(b))
}

/**
 * Check if a === b
 */
export const equals = (a: any, b: any): boolean => {
  return toBig(a).eq(toBig(b))
}

/**
 * Sum an array of numbers
 */
export const sum = (values: any[]): Big => {
  return values.reduce((acc, val) => add(acc, val), new Big(0))
}

/**
 * Get absolute value
 */
export const abs = (value: any): Big => {
  return toBig(value).abs()
}

/**
 * Round to specified decimal places
 */
export const round = (value: any, decimalPlaces: number = 2): Big => {
  return toBig(value).round(decimalPlaces, Big.roundHalfUp)
}

/**
 * Convert Big number to formatted string with fixed decimal places
 */
export const toFixed = (value: any, decimalPlaces: number = 2): string => {
  return toBig(value).toFixed(decimalPlaces)
}

/**
 * Convert Big number to regular number (use with caution)
 */
export const toNumber = (value: Big | any): number => {
  if (value instanceof Big) {
    return value.toNumber()
  }
  return Number(value) || 0
}

/**
 * Format file size from bytes to human-readable format
 * Uses big.js for precise calculations
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  // Use big.js for division and rounding
  const value = divide(bytes, Math.pow(k, i))
  const rounded = round(value, 2)

  return `${rounded.toString()} ${sizes[i]}`
}

/**
 * Validate if a value is a valid number
 */
export const isValidNumber = (value: any): boolean => {
  try {
    const bigValue = toBig(value)
    return bigValue.constructor === Big
  } catch {
    return false
  }
}

/**
 * Check if value is finite and valid
 */
export const isFinite = (value: any): boolean => {
  try {
    toBig(value)
    return true
  } catch {
    return false
  }
}
